import React, {useEffect, ChangeEvent, useState} from "react";
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import {Trans, useTranslation} from "react-i18next";

// list
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

// toggle button
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';

// for styles
import {
  Button,
  Backdrop
} from "@material-ui/core/";

import {
  getFlightCodeLabel,
  getFlightCodeByLabel,
  getFlightTime,
  getMultiAirports,
  isAnaBoundaryCrossing,
  isVacancy,
  getFlightNumberForDisplay,
  getArrivalAirports,
} from "../../utils/flight";

// loading
import LoadingOverlay from 'react-loading-overlay-ts';

// sass
import "../../scss/common.scss";
import "../../scss/component.scss";
import "../../scss/component/form.scss";
import "../../scss/component/icon.scss";

import Modal from "@material-ui/core/Modal";
import {
  Flights,
  FlightType,
  FReservationFlightEntity,
  FReservationFlightsEntity
} from "../../types/Flight";

// for api
import { signalApiClient } from "../../utils/apiClient";
import { PlanSearch } from "../../types/Search";
import dayjs from "dayjs";

import {airportGroup, departureAirCode} from "../../config/Airport";
import {addOtsRate, basicTotalPriceChangeForFlight} from "../../utils/price";
import {
  CheepsFlight,
  flightSearchResponse,
  FReservationConnectionFlight, FReservationFlight,
  FReservationFlights
} from "../../types/FlightV2";
import {
  createReserveFlightNumber,
  filterFlightTime,
  getCheepsFlight,
  getConnectionFlights,
  getDiffFlightPrice, getFlightClassLabel, isChangeAirport,
  isConnection, lastCurrentFlight,
  sortFlights
} from "../../utils/flightV2";
import {PlanSelectFormData} from "../../types/planSelectForm";
import {hotelList} from "../../types/Hotel";
import _ from "lodash";
import {checkOnTimeForFlight} from "../../utils/checkOnTimeForFlight";
import FlightApproveModal from "./FlightApproveModal";
import {IActivity} from "../../types/Activity";
import SectionRetry from "../SectionRetry";
import flightApproveModal from "./FlightApproveModal";

// モーダルに何か渡したい場合はここ追加する
type Props = {
  planSelectFormData: PlanSelectFormData,
  pageRef: string,
  isShow: boolean,
  callback: () => void,
  changeAirportCallback: (airport:string, action: string) => void,
  search: PlanSearch | undefined,
  currentFlights: FlightType | undefined,
  changeFlightCallback: (airline:string, action:string, currentFlights: FReservationFlight[], airport: string | null | undefined ) => void,
  profitRate: string | undefined,
}

const FlightModalApi: React.FC<Props> = ({
    planSelectFormData,
    pageRef,
    isShow,
    callback,
    changeAirportCallback,
    changeFlightCallback,
    search,
    currentFlights,
    profitRate,
  }) => {
  const { t, i18n } = useTranslation();
  const [controller, setController] = useState(new AbortController())
  
  // 空港の配列
  const [airports, setAirports] = React.useState<string[]>();
  const [from , setFrom] = React.useState(search?.from ?? '');

  // ローディング
  const [isLoading, setLoading] = React.useState(false);
  const [changeSearch, setChangeSearch] = React.useState(false);

  // 全てのフライト
  const [flights, setFlights] = React.useState<FReservationFlights[] | undefined>(undefined);
  /**
   * 一部（または全部）の便を取得できなかったとき。 issue #2992 / 報告書 §8 No.4
   * ★従来は status 200 のまま便が無言で欠落し、画面上は「該当便なし」と同じ見え方だった。
   *   利用者は繰り返し検索し直し、そのぶん上流コール（1リクエストあたり JAL 平均6.9本）が
   *   再発生していた（2026-08-31 実測）。
   */
  const [flightError, setFlightError] = React.useState<{retryAfterSec: number, partial: boolean} | undefined>(undefined);
  const [defaultFlights, setDefaultFlights] = React.useState<FReservationFlights[] | undefined>();

  // 選択（更新やコールバックで渡す用）
  const [selectedFlights, setSelectedFlights] = React.useState<FReservationFlight>();

  const [airport, setAirport] = React.useState<string | null | undefined>();
  const [arrivals, setArrivals] = React.useState<string[]>();     // 到着空港
  // 復路の出発空港（島側）変更（issue #3015 拡張・オープンジョー統合）。
  // 従来の「出発空港を変更する」導線をこのモーダルに統合したもの。復路(outbound)でのみ使用。
  const [departureAirport, setDepartureAirport] = React.useState<string | null | undefined>();

  // all,direct,connecting
  const [flightFilter, setFlightFilter] = React.useState("all");

  // 「確定する」ボタンのハンドリング
  const [buttonEnable, setButtonEnable] = React.useState(false);
  const [cheepsFlight, setCheepsFlight] = React.useState<CheepsFlight>({
    flight: undefined,
    index: 0,
    price: 0
  });

  // 見つからない場合のメッセージ
  const [mixedMessage, setMixedMessage] = React.useState(t("データが見つかりませんでした。"));
  const [connectingMessage, setConnectingMessage] = React.useState(t("データが見つかりませんでした。"));

  useEffect(() => {
    if (isShow && search) {
      initFlightSelect();
      if (search.c_airport_to && search.isChange && currentFlights?.action === 'outbound') {
        setAirport(search.c_airport_to);
        setArrivals(getArrivalAirports(search.c_airport_to, true, false));
      } else {
        setAirport(search.from);
        setArrivals(getArrivalAirports(search.from, true, false));
      }
      // 復路の出発空港（島側）は「現在のフライトの発地」を初期値にする（issue #3015 拡張）。
      // searchParams からではなく現行便から初期化することで、発地変更後に再度開いても
      // 変更前に巻き戻らない（旧「出発空港を変更する」分離導線で起きていた巻き戻りの根治）。
      // 確認画面（page="confirm"）限定。PlanSelectTop から開く場合は従来どおり search 由来の発地を使う
      if (currentFlights?.action === 'outbound' && currentFlights?.page === 'confirm') {
        const currentDep = currentFlights?.flight?.[0]?.depapo;
        setDepartureAirport(
          currentDep ?? (search.isChange && search.c_airport_from ? search.c_airport_from : search.to),
        );
      } else {
        setDepartureAirport(undefined);
      }
      (async () => {
        await searchFlights();
      })()
    }
  }, [isShow])

  // モーダルを閉じたら出発空港（島側）の変更状態を破棄する（issue #3015 拡張）。
  // 「閉じる」ボタンは reset() を通らず callback を直接呼ぶため、ここでクリアしないと
  // 「発地を変更→確定せず閉じる→開き直し」で前回セッションの発地が state に残り、
  // 開き直し直後の初回検索（クロージャが残留 state を掴む）が旧発地で走ってしまう
  // （セレクタは現行便の発地を表示するのに一覧だけ前回の発地、という不整合になる）。
  useEffect(() => {
    if (!isShow) {
      setDepartureAirport(undefined);
    }
  }, [isShow]);

  useEffect(() => {
    if (changeSearch) {
      (async () => {
        await searchFlights();
      })()
      setChangeSearch(false)
    }
  }, [changeSearch]);
  
  const searchFlights = async () => {

    if (search !== undefined && isShow) {
      initFlightSelect();

      // フライト
      if(currentFlights && currentFlights.currentNumbers && currentFlights.currentNumbers.length > 0) {
        if (isAnaBoundaryCrossing(currentFlights.airline, search?.start ?? "", search?.end ?? "")) {
          setLoading(false);
          setSelectedFlights(undefined);
          setFlights([]);
          setDefaultFlights([]);
          setButtonEnable(true);

          return;
        }
        setLoading(true)

        // 復路
        const outboundTo = () => {
          const result = search?.isChange ? getFlightCodeByLabel(search?.c_airport_to) : getFlightCodeByLabel(search?.from)
          return airport ? getFlightCodeByLabel(airport) : result
        }
        // 往路
        const inboundFrom = () => {
          const result = getFlightCodeByLabel(search?.from)
          return airport ? getFlightCodeByLabel(airport) : result
        }
        const outboundFrom = () => {
          // 復路の出発空港（島側）の決定順（issue #3015 拡張・オープンジョー統合）:
          //   1) モーダル内で明示的に変更した発地
          //   2) 現行便の発地（確認画面のみ）
          //   3) 従来どおり search 由来
          // 2) が必要なのは、モーダルを開いた直後の検索では departureAirport の state 反映が
          // 間に合わず（setState は非同期で、この関数のクロージャは旧値を掴む）、
          // オープンジョー変更済みの旅程を開き直したときに「セレクタは変更後の空港なのに
          // 一覧は変更前の発地で検索される」表示不整合が起きるため。
          if (departureAirport) return getFlightCodeByLabel(departureAirport);
          if (currentFlights?.page === 'confirm' && currentFlights?.flight?.[0]?.depapo) {
            return currentFlights.flight[0].depapo;
          }
          const result = search?.isChange ? getFlightCodeByLabel(search?.c_airport_from) : getFlightCodeByLabel(search?.to);
          return result
        }

        // comb
        const from = currentFlights.action === "inbound" ? inboundFrom() : outboundFrom()
        const to = currentFlights.action === "inbound" ? search?.to : outboundTo()

        setAirports(getMultiAirports(from, true, currentFlights.action == "inbound"));

        const useParam = {
          "depapo": from,
          "arrapo": to,
          "departure_date": currentFlights.action === "inbound" ? (search?.start).replace(/-/g, "") : (search?.end).replace(/-/g, "")// "20220622" format
        }

        const abortController = new AbortController()
        setController(abortController);
        const tripInboundDate = (search?.start ?? "").replace(/-/g, "");
        const tripOutboundDate = (search?.end ?? "").replace(/-/g, "");
        await signalApiClient(abortController).post('/air/flight-search', JSON.stringify({
          "action": currentFlights.action,
          "inboundDepartureDate": tripInboundDate,
          "outboundDepartureDate": tripOutboundDate,
          "inboundDepapo": useParam.depapo,
          "inboundArrapo": useParam.arrapo,
          "outBoundDepapo": useParam.depapo,
          "outBoundArrapo": useParam.arrapo,
          "air_code": currentFlights.airline
        })).then((response: AxiosResponse<flightSearchResponse>) => {
          // Loading
          setLoading(false)
          const fs = response.data;
          // ★取得できなかった区間があれば区画エラーを立てる（issue #2992 / 報告書 §8 No.4）。
          //   取れた便は表示したうえで「一部の便を取得できませんでした」を出す。
          const partialErrors = fs.errors ?? [];
          if (partialErrors.length > 0 || fs.status !== 200) {
            setFlightError({
              retryAfterSec: fs.retryAfterSec ?? 0,
              partial: partialErrors.length > 0 && fs.status === 200,
            });
          } else {
            setFlightError(undefined);
          }
          if (fs.status === 200 && fs.flight && fs.flight.FReservationFlights ) {
            let _flights = sortFlights(fs.flight.FReservationFlights)
            
            // フライト時間制御チェック(絞り込み)
            if (currentFlights.action === "inbound") {
              _flights = filterFlightTime(fs.flight.FReservationFlights, planSelectFormData.sellerProduct?.arrivalTimeControl, "arrival");
            } else if (currentFlights.action === "outbound") {
              _flights = filterFlightTime(fs.flight.FReservationFlights, planSelectFormData.sellerProduct?.departureTimeControl, "departure");
            }

            const cheeps = getCheepsFlight(currentFlights.airline, _flights, planSelectFormData.searchParams.roomDetail);
            setCheepsFlight({...cheeps})
            setFlights([..._flights]);
            setDefaultFlights([..._flights]);

            // 復路の出発空港（島側）を現行便から変更した場合も最安便を自動選択する（issue #3015 拡張）。
            // isChangeAirport は inbound 専用のため、これが無いと選択が旧便（変更前の発地）のまま残り、
            // ユーザーが便を選び直さずに確定すると発地変更が無言で捨てられる
            const isOutboundDepartureChanged =
              currentFlights.action === "outbound" &&
              !!currentFlights.flight?.[0]?.depapo &&
              from !== currentFlights.flight[0].depapo;

            if (_flights.length > 0 &&
                (isChangeAirport(search, currentFlights.action, from) || isOutboundDepartureChanged)) {
              setSelectedFlights(_flights[cheeps.index].FReservationFlight[0]);
            } else if (_flights.length === 0) {
              setSelectedFlights(undefined);
            }
            setButtonEnable(true);
          } else {
            setSelectedFlights(undefined);
            setFlights([]);
          }

        })
          .catch(function (error) {
            setLoading(false)
            setSelectedFlights(undefined);
            setFlights([]);
            // ★「便が無い」ではなく「取得できなかった」として扱う（issue #2992 / 報告書 §8 No.4）
            setFlightError({
              retryAfterSec: error?.response?.data?.retryAfterSec ?? 0,
              partial: false,
            });
            console.log(error);
          });
      }
    }
  }

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const flightOpen = Boolean(anchorEl);

  const departureLabel = () => {
    if (!isInbound() && search?.isChange && search?.c_airport_from) {
      return getFlightCodeLabel(search?.c_airport_from);
    }

    return getFlightCodeLabel(search?.to);
  }
  
  const handleMenuItemClick = (
      e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (airport !== e.target.value) {
      setAnchorEl(null);
      const updateAirport = e.target.value;
      setAirport(updateAirport)
      setFrom(updateAirport)
      setChangeSearch(true)
    }
  };

  // 復路の出発空港（島側）変更（issue #3015 拡張・オープンジョー統合）。
  // 変更したら現行フィルタ状態のまま新しい発地で再検索する。
  const handleDepartureChange = (
      e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (departureAirport !== e.target.value) {
      setDepartureAirport(e.target.value);
      setChangeSearch(true);
    }
  };

  const isInbound = ():boolean => {
    if(currentFlights && airport) {
      return currentFlights.action == 'inbound'
    } else {
      return false
    }
  }

  const isSelected = (checkFlight: FReservationFlight): boolean => {
    const checkFlightNumber = createReserveFlightNumber(checkFlight);
    const selectFlightNumber = createReserveFlightNumber(selectedFlights);
    return checkFlightNumber === selectFlightNumber;
  }

  /**
   * 初期フライト選択設定
   * @param currentFlightss
   */
  const initFlightSelect = () => {
    setFlights(undefined);
    if(currentFlights == null) return;
    setSelectedFlights({...currentFlights.flight[0]});

  }

  const handleFlightSelect = (selectedFlights: FReservationFlight | null, isMixed: boolean, airline: string|undefined) => {
    if(selectedFlights == null || !flights) return;
    setSelectedFlights({...selectedFlights})
    if(flights.length > 0) {
      setConnectingMessage(t("データが見つかりませんでした。") ?? "データが見つかりませんでした。");
    }
  }

  //直行/乗継ぎ用トグルボタン
  const toggleCreatecurrentFlights = (
      label: string,
      value: string,
  ) => {
    return { label, value };
  };
  const toggleRows = [
    toggleCreatecurrentFlights(t("すべて"), "all",),
    toggleCreatecurrentFlights(t("直行便のみ"), "direct",),
    toggleCreatecurrentFlights(t("乗継便のみ"), "connecting",),
  ];
  const [flightToggleState, setFlightToggleState] = React.useState(() => ['all']);
  const handleFlightToggle = (event: React.MouseEvent<HTMLElement>, newFlightToggle: string[]) => {
    setFlightToggleState(newFlightToggle);
    setFlightFilter(newFlightToggle.toString());
    if(defaultFlights && defaultFlights.length > 0) {
      // 乗継便
      if (newFlightToggle.toString() === "connecting") {
        setFlightFilter("connecting");
      }
      // 直行便
      if (newFlightToggle.toString() === "direct") {
        setFlightFilter("direct");
      }
      // すべて
      if (newFlightToggle.toString() === "all") {
        setFlightFilter("all");
      }
    }
  }

  //出発時間用トグルボタン
  const toggleTimeCreatecurrentFlights = (
    label: string,
    value: string,
    fromTime: string,
    toTime: string,
  ) => {
    return { label, value, fromTime, toTime };
  };
  const toggleTimeRows = [
    toggleTimeCreatecurrentFlights(t("すべて"), "all", "", ""),
    toggleTimeCreatecurrentFlights(t("5時〜8時台"), "5_to_8", "0500", "0859"),
    toggleTimeCreatecurrentFlights(t("9時〜12時台"), "9_to_12", "0900", "1259"),
    toggleTimeCreatecurrentFlights(t("13時〜17時台"), "13_to_17", "1300", "1759"),
    toggleTimeCreatecurrentFlights(t("18時〜22時台"), "18_to_22", "1800", "2259"),
  ];
  const [flightTimeToggleState, setFlightTimeToggleState] = React.useState(() => ['all']);
  const handleFlightTimeToggle = (event: React.MouseEvent<HTMLElement>, newFlightTimeToggle: string[]) => {
    setFlightTimeToggleState(newFlightTimeToggle);
  };

  /**
   * 絞り込み
   */
  useEffect(() => {
    let updateFlights = _.cloneDeep(defaultFlights);

    if (updateFlights) {

      // 乗継便
      if(flightFilter === "connecting") {
        setFlightFilter("connecting");
        updateFlights = updateFlights.filter((flight) => {
          return flight.FReservationFlight[0].FReservationConnectionFlight.length > 0
        })
      }
      // 直行便
      if(flightFilter === "direct") {
        setFlightFilter("direct");
        updateFlights = updateFlights.filter((flight) => {
          return flight.FReservationFlight[0].FReservationConnectionFlight.length === 0
        })
      }


      if(flightTimeToggleState) {
        const result = toggleTimeRows.find((t) => t.value === flightTimeToggleState.toString())
        if (result && result.value !== "all") {
          updateFlights = updateFlights.filter((flights) => {
            return Number(flights.FReservationFlight[0].dep) >= Number(result.fromTime) && Number(flights.FReservationFlight[0].dep) <= Number(result.toTime);
          })
        }
      }

      setFlights([...updateFlights])
    }

  }, [flightTimeToggleState, flightFilter])

  // 往路なら出発日を、復路なら到着日を取得する
  const getFlightScheduleDate = () => {
    if (search && currentFlights) {
      return currentFlights.action === "inbound" ? search.start : search.end
    }
    return search?.start;
  }

  /**
   * フライト確定ボタン時処理
   */
  const [missForFlightActivity, setMissForFlightActivity] = React.useState<IActivity[]>([]);
  const [isFlightApproveModal, setIsFlightApproveModal] = React.useState(false);
  const decide = () => {
    if(!buttonEnable || !selectedFlights) {
      callback()
      return
    }
    if(currentFlights && selectedFlights) {
      // フライトに間に合うか確認
      const missActivity = checkOnTimeForFlight(planSelectFormData.activity, selectedFlights, currentFlights.action);
      setMissForFlightActivity(missActivity);

      if (missActivity.length > 0) {
        setIsFlightApproveModal(true);
      } else {
        updateSelectFlight();
      }
      
    }
  }
  
  const updateSelectFlight = () => {
    setIsFlightApproveModal(false);
    setFlights(undefined);
    setFlightFilter('all');
    setFlightToggleState(['all']);
    setFlightTimeToggleState(['all']);
    if (currentFlights && selectedFlights) {
      // 出発・到着空港変更時 更新処理
      if(airport) {
        changeFlightCallback(currentFlights.airline,  currentFlights.action, [selectedFlights], airport)
      } else {
        // セクション部分の更新
        changeFlightCallback(currentFlights.airline,  currentFlights.action, [selectedFlights], airport)
      }
    }
  }

  // for text label
  const getFlightTextLabel = () => {
    if(currentFlights) {
      // 往路
      if(currentFlights.action === "inbound") {
        return t("往路")
      }
      // 復路
      if(currentFlights.action === "outbound") {
        return t("復路")
      }
    }
    return t("往路")
  }

  const reset = () => {
    controller.abort();
    setFlights(undefined)
    setFlightFilter('all')
    setFlightToggleState(['all']);
    setFlightTimeToggleState(['all']);
    if (currentFlights) {
      setAirport(undefined);
      setDepartureAirport(undefined);
      setFrom(search?.from ?? '')
      setSelectedFlights({...currentFlights.flight[0]})
      setFlights(undefined);
    }
    callback();
  }

  return (
    <>
      {/* モーダル：フライト選択 */}
      <div>
        <Modal
          open={isShow}
          onClose={reset}
          className="modal"
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 300,
          }}
        >
          <div className="modal-fixed">
            <div className="modal-fixed-heading">
              <p className="modal-fixed-heading-text">{getFlightTextLabel()}{t("のフライト変更")}</p>
              <p className="modal-fixed-close">
                <Button onClick={callback} className="modal-fixed-close-button">
                  <i className="modal-fixed-close-button-icon"></i>{t("閉じる")}
                </Button>
              </p>
            </div>

            <div className="flight-modal-fixed-content">
              <div className="wrap-content-900 wrap-padding-md">
                <p className="heading-2 fz-24-md mt-24 mb-24 mt-40-md">{currentFlights?.airline}{t("から選択")}</p>
                <dl>
                  <div className="d-f-md ai-c">
                    <dt className="c-navy fz-13 fz-16-md fw-b mb-8 mb-0-md fx-sh w-110-md">{t("利用空港")}</dt>
                    <dd className="fx-1">
                      <div className="d-f ai-e">
                        {!isInbound() && // アウトバウンド（復路）だったら
                          <>
                            {/* 到着空港セレクタと幅を揃えるため fx-1 は付けない（select は width:100%） */}
                            <div>
                              <p className="c-blue_gray fz-12 fz-14-md mb-5">{t("出発空港")}</p>
                              <div className="form-select_list-wrap">
                                {/* 復路の出発空港（島側）変更（issue #3015 拡張・オープンジョー統合）。
                                    従来は固定表示だったが、出発空港セレクトに変更して発地を変えられるようにした */}
                                {currentFlights?.page === "confirm" ? (
                                  <div className="select_box-default">
                                    <select className={"form-select_list"}
                                            name="departureAirport"
                                            value={departureAirport ?? ""}
                                            onChange={(e) => handleDepartureChange(e)}
                                    >
                                      {airportGroup && airportGroup.map((group: any, gIndex: number) => (
                                        <optgroup label={group.groupName} key={"dep-grp-" + gIndex}>
                                          {Object.keys(group.airportList).map((aircode: any, i: number) => (
                                            <option className={"form-select_list-options"} value={aircode}
                                                    key={"dep-" + gIndex + "-" + i}>{t(`Airport.${group.airportList[aircode]}`)}</option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                  </div>
                                ) : (
                                  <p className="form-select_list-text">{t(`Airport.${departureLabel() ?? ""}`)}</p>
                                )}
                              </div>
                            </div>
                            <i className="icon-right-arrow fx-sh fz-12 ml-16 mr-16 mb-16"></i>
                          </>
                        }
                        <div>
                          <p className="c-blue_gray fz-12 fz-14-md mb-8">{isInbound() ? t('出発空港') : t('到着空港')}</p>
                          <div className="form-select_list-wrap">
                            {isInbound() && currentFlights?.page === "confirm" ?
                              <>
                                <p className="form-select_list-text">{t(`Airport.${getFlightCodeLabel(search?.from) ?? ""}`)}</p>
                              </>
                              : isInbound() ?
                                <>
                                  <div className="select_box-default">
                                    <select className={"form-select_list"}
                                            name="prefecture"
                                            value={airport ?? ""}
                                            onChange={(e) => handleMenuItemClick(e)}
                                    >
                                      {
                                        // 出発空港
                                        airportGroup && airportGroup.map((currentFlights: any, index: number) => (
                                          <optgroup label={currentFlights.groupName} key={index}>
                                            {Object.keys(currentFlights.airportList).map((aircode: any, i: number) => (
                                              <option className={"form-select_list-options"} value={aircode}
                                                      key={i}>{t(`Airport.${currentFlights.airportList[aircode]}`)}</option>
                                            ))}
                                          </optgroup>
                                        ))
                                      }
                                    </select>
                                  </div>
                                </>
                                :
                                // 出発空港
                                <>
                                  <div className="select_box-default">
                                    <select className={"form-select_list"}
                                            name="prefecture"
                                            value={airport ?? ""}
                                            onChange={(e) => handleMenuItemClick(e)}
                                    >
                                      {
                                        arrivals ? arrivals.map((airCode, index) => (
                                          <option key={"c_airport" + index} value={airCode}>
                                            {t(`Airport.${getFlightCodeLabel(airCode) ?? ""}`)}
                                          </option>
                                        )) : ""
                                      }
                                    </select>
                                  </div>
                                </>
                            }
                          </div>
                        </div>

                        {isInbound() && // インバウンドだったら
                          <>
                            <i className="icon-right-arrow fx-sh fz-12 ml-16 mr-16 mb-16"></i>
                            <div className="fx-1">
                              <p className="c-blue_gray fz-12 fz-14-md mb-5">{t("到着空港")}</p>
                              <div className="form-select_list-wrap">
                                <p className="form-select_list-text">{t(`Airport.${getFlightCodeLabel(search?.to) ?? ""}`)}</p>
                              </div>
                            </div>
                          </>
                        }
                      </div>
                    </dd>
                  </div>

                  <div className="d-f-md ai-c mt-16 mt-24-md">
                    <dt className="c-navy fz-13 fz-16-md fw-b mb-8 mb-0-md fx-sh w-110-md">{t("直行/乗継ぎ")}</dt>
                    <dd className="fx-1">
                      <ToggleButtonGroup
                        value={flightToggleState}
                        exclusive
                        onChange={handleFlightToggle}
                        aria-label="all"
                        className="box-toggle_button"
                      >
                        {toggleRows.map((row, index) => (
                          <ToggleButton key={"toggle_f" + index} value={row.value} aria-label={row.value}
                                        className="box-toggle_button-item">
                            {t(row.label)}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </dd>
                  </div>

                  <div className="d-f-md ai-c mt-16 mt-24-md">
                    <dt className="c-navy fz-13 fz-16-md fw-b mb-8 mb-0-md fx-sh w-110-md">{t("出発時間帯")}</dt>
                    <dd className="fx-1">
                      <ToggleButtonGroup
                        value={flightTimeToggleState}
                        exclusive
                        onChange={handleFlightTimeToggle}
                        aria-label="all"
                        className="box-toggle_button"
                      >
                        {toggleTimeRows.map((row, index) => (
                          <ToggleButton key={"toggle_t" + index} value={row.value} aria-label={row.value}
                                        className="box-toggle_button-item">
                            {t(row.label)}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </dd>
                  </div>
                </dl>

                {/* フライト選択 */}
                <LoadingOverlay
                  active={isLoading}
                  spinner
                  className="loading-small-height"
                >

                  <form action="">
                    {/* 直行便 */}
                    {flightToggleState ?
                      <div className="box_select-flight-schedule label-mulch">

                        <ul className="list-note mt-24 mb-50">
                          <li>{t("フライト差額は、選択済みフライトからの差額（1人あたり）を目安として表示しています。最終的な旅行代金は、旅行代金合計にてご確認ください。")}</li>
                          {currentFlights?.airline === "ANA" &&
                            <>
                              <li>{t("プレミアムクラスは選択できません。")}</li>
                              <li>
                                <Trans i18nKey={"共同運航便（コードシェア便）について詳しくはこちら"} components={{link: <a href='https://www.ana.co.jp/ja/jp/travel/dom_pkg/info/codeshare/' target='_blank' rel='noopener noreferrer' /> }} />
                              </li>
                            </>
                          }
                          {currentFlights?.airline === "JAL" &&
                            <>
                              <li>{t("ファーストクラスは選択できません。")}</li>
                            </>
                          }
                        </ul>

                        <div className="box_select-flight-schedule-title">
                          <p
                            className="box_select-flight-schedule-date">{dayjs(getFlightScheduleDate(), "YYYYMMDD").locale(i18n.language).format(t('YYYY年MM月DD日（ddd）') ?? 'YYYY年MM月DD日（ddd）')}</p>
                          <div className="box_select-flight-schedule-difference">{t("差額")}
                            <span className="fz-10 fz-12-md">（{t("１人あたり")}）</span>
                          </div>
                        </div>

                        <ul className="box_select-flight-schedule-list">
                          {flights && flights.length > 0 ? flights?.map((flight, fIndex) => (<>
                            <li
                              className={flight.FReservationFlight.length > 1 ? "box_select-flight-schedule-list-item mulch" : "box_select-flight-schedule-list-item"}>
                              <div className="box_select-flight-schedule-list-item-time_table">
                                {isConnection(flight.FReservationFlight[0]) ? (<>
                                  <div className="box_select-flight-schedule-list-item-time_table-label orange">
                                    <span
                                      className="box_select-flight-schedule-list-item-time_table-label-text">{t("乗継便")}</span>
                                  </div>
                                </>) : (<>
                                  <div className="box_select-flight-schedule-list-item-time_table-label green">
                                    <span
                                      className="box_select-flight-schedule-list-item-time_table-label-text">{t("直行便")}</span>
                                  </div>
                                </>)}

                                <div className="box_select-flight-schedule-list-item-time_table-time_schedule">
                                  <ul className="box_select-flight-schedule-list-item-time_table-time_schedule-list">
                                    <li key={"flight-" + fIndex}
                                        className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item">
                                      <p
                                        className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-heading">{getFlightNumberForDisplay(currentFlights?.airline, flight.FReservationFlight[0].air_code, flight.FReservationFlight[0].air_sub_no, i18n.language)}</p>
                                      <ul
                                        className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time">
                                        <li key={"flight-dep-" + fIndex}
                                            className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                          <span
                                            className="fz-10 fz-12-md w-5em">{t(`Airport.${getFlightCodeLabel(flight.FReservationFlight[0].depapo) ?? ""}`)?.replace(/(?:空港|Airport)/gi, '')}</span><span
                                          className="fz-18 fz-24-md c-navy fw-b mb-2 mb-4-md">{getFlightTime(flight.FReservationFlight[0].dep)}</span>
                                        </li>

                                        <li key={"flight-arv-" + fIndex}
                                            className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                          <span
                                            className="fz-10 fz-12-md w-5em">{t(`Airport.${getFlightCodeLabel(flight.FReservationFlight[0].arrapo) ?? ""}`)?.replace(/(?:空港|Airport)/gi, '')}</span><span
                                          className="fz-14 fz-20-md c-navy fw-b mb-4 mb-6-md">{getFlightTime(flight.FReservationFlight[0].arv)}</span>
                                        </li>
                                      </ul>
                                    </li>

                                    {getConnectionFlights(flight.FReservationFlight[0].FReservationConnectionFlight, []).map((connectionFlight, cIndex) => (<>

                                      <li key={"flight-connection-" + cIndex}
                                          className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item">
                                        <p
                                          className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-heading">{getFlightNumberForDisplay(currentFlights?.airline, connectionFlight.air_code, connectionFlight.air_sub_no, i18n.language)}</p>
                                        <ul
                                          className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time">
                                          <li key={"flight-connection-dep-" + cIndex}
                                              className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                            <span
                                              className="fz-10 fz-12-md w-5em">{t(`Airport.${getFlightCodeLabel(connectionFlight.depapo)}`)?.replace(/(?:空港|Airport)/gi, '')}</span>
                                            <span
                                              className="fz-18 fz-24-md c-navy fw-b mb-2 mb-4-md">{getFlightTime(connectionFlight.dep)}</span>
                                          </li>

                                          <li key={"flight-connection-arv-" + cIndex}
                                              className="box_select-flight-schedule-list-item-time_table-time_schedule-list-item-time-item">
                                            <span
                                              className="fz-10 fz-12-md w-5em">{t(`Airport.${getFlightCodeLabel(connectionFlight.arrapo)}`)?.replace(/(?:空港|Airport)/gi, '')}</span>
                                            <span
                                              className="fz-14 fz-20-md c-navy fw-b mb-4 mb-6-md">{getFlightTime(connectionFlight.arv)}</span>
                                          </li>
                                        </ul>
                                      </li>
                                    </>))}
                                  </ul>
                                </div>

                              </div>
                              <div className="box_select-flight-schedule-list-item-seat">
                                {flight.FReservationFlight.map((FReservationFlight, seatIndex) => (<>
                                  <div
                                    className={isVacancy(currentFlights?.airline, FReservationFlight, planSelectFormData.searchParams.roomDetail) ? 'box_select-flight-schedule-list-item-seat-content' : 'box_select-flight-schedule-list-item-seat-content invalid'}>
                                    <ul className="box_select-flight-schedule-list-item-seat-content-list">
                                      <li key={"flight-input-" + fIndex + "-" + seatIndex}
                                          className="box_select-flight-schedule-list-item-seat-content-list-label">
                                        <input
                                          type="radio"
                                          name="df"
                                          id={"flight-input-id" + fIndex + "-" + seatIndex}
                                          value={createReserveFlightNumber(FReservationFlight)}
                                          checked={isSelected(FReservationFlight)}
                                          disabled={!isVacancy(currentFlights?.airline, FReservationFlight, planSelectFormData.searchParams.roomDetail)}
                                          onChange={(event) => handleFlightSelect(FReservationFlight ? FReservationFlight : null, true, currentFlights?.airline)}
                                        />
                                        <label htmlFor={"flight-input-id" + fIndex + "-" + seatIndex}
                                               className="fz-12 fz-14-md c-navy fw-b">{t(getFlightClassLabel(FReservationFlight.class_code))}</label>
                                      </li>
                                      <li className="box_select-flight-schedule-list-item-seat-content-list-status">
                                        <label htmlFor={"flight-input-id" + fIndex + "-" + seatIndex}
                                               className="box_select-flight-schedule-list-item-seat-content-list-status-label">
                                          <span className="d-b fz-10 fz-14-md fw-b c-navy">{t("空席")}</span><i
                                          className={isVacancy(currentFlights?.airline, FReservationFlight, planSelectFormData.searchParams.roomDetail) ? 'icon-possible ml-8-md mr-8-md' : 'icon-impossible ml-8-md mr-8-md'}></i></label>
                                      </li>
                                      <li key={"flight-price-" + fIndex + "-" + seatIndex}
                                          className="box_select-flight-schedule-list-item-seat-content-list-result">
                                        <label htmlFor={"flight-input-id" + fIndex + "-" + seatIndex}><p
                                          className="box_select-flight-schedule-list-item-seat-content-list-result-text">{getDiffFlightPrice(FReservationFlight, currentFlights, planSelectFormData)}<span
                                          className="fz-12">{t("円")}</span></p></label>
                                      </li>
                                    </ul>
                                  </div>
                                </>))}
                              </div>
                            </li>
                          </>)) : (<li key={"dnocurrentFlights"} className="p-16">{
                            /* ★取得できなかった場合は「該当便なし」の文言を出さない（issue #2992） */
                            flightError ? "" : mixedMessage
                          }</li>)}
                        </ul>
                        {/* 便の取得に失敗した区画の再読み込み（issue #2992 / 報告書 §8 No.4） */}
                        {flightError && !isLoading && (
                          <SectionRetry
                            retryAfterSec={flightError.retryAfterSec}
                            isLoading={isLoading}
                            onRetry={() => { searchFlights(); }}
                            message={(flightError.partial
                              ? t("一部の便を取得できませんでした。")
                              : t("便を取得できませんでした。")) ?? undefined}
                          />
                        )}
                      </div>

                      : <></>}

                    <div className="flight-modal-fixed-bottom">
                      {/* <Button disabled={buttonEnable} onClick={() => decide()} className="button-medium-light_blue padding-large w-100per">確定する</Button> */}
                      <button type="button" onClick={() => decide()}
                              className="button-medium-orange padding-large w-100per">{t("確定する")}
                      </button>

                      <div className="wrap-content-900-no_padding ">
                        <div className="box-white fixed-padding">
                          <div className="d-f ai-c jc-sb jc-fe-md">
                            <p className="fw-b pr-24">
                              {t("現在の旅行代金合計")}<br/>
                              <span className="fz-12 fw-n">（※{t("申込人数分の総額")}）</span>
                            </p>
                            <p className="fz-24 fz-28-md c-red fw-b">
                              {planSelectFormData.basicPriceTotal > 0 ? (
                                <>
                                  {basicTotalPriceChangeForFlight(planSelectFormData, {flight: selectedFlights, bound: isInbound() ? 'inbound' : 'outbound'})?.toLocaleString()}
                                  <span className="fz-14 fz-16-md">{t("円")}({t("税込")})</span>
                                </>
                              ) : (<p className="fw-b">{t("計算中")}</p>)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </LoadingOverlay>
              </div>
            </div>

          </div>
        </Modal>
      </div>
      <FlightApproveModal
        isShow={isFlightApproveModal}
        onClickClose={() => {setIsFlightApproveModal(false); setMissForFlightActivity([]) }}
        callbackDiscountApprove={() => {updateSelectFlight()}}
        missForFlightActivity={missForFlightActivity}
        flightType={currentFlights?.action}
      />
      {/* モーダルフライト変更 */}
    </>
  );
};

export default FlightModalApi;
